import * as React from 'react'
import { cn } from '../../lib/cn'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './Tooltip'

/**
 * PolymorphicBadge — tiny teal pill marking a method-aware block.
 *
 * Matches the `.sys-chip-method-tag` element in the Phase 2 mockup.
 * When `tooltip` is supplied, wraps in a Radix Tooltip so hovering
 * reveals an explanation.
 *
 * Composes: Tooltip (atom).
 */
export interface PolymorphicBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Pill label. Defaults to "Poly". */
  label?: string
  /** Tooltip text shown on hover. No tooltip rendered when omitted. */
  tooltip?: string
}

const PolymorphicBadgePill = React.forwardRef<HTMLSpanElement, PolymorphicBadgeProps>(
  ({ label = 'Poly', tooltip: _tooltip, className, ...props }, ref) => (
    <span
      ref={ref}
      role="img"
      aria-label={`Polymorphic block: ${label}`}
      className={cn(
        'inline-flex items-center',
        'font-mono text-[8.5px] uppercase tracking-[0.12em]',
        'px-[6px] py-[2px] rounded-sm',
        // Teal tokens matching .sys-chip-method-tag
        'text-[#2dd4bf] bg-[rgba(45,212,191,0.06)] border border-[rgba(45,212,191,0.22)]',
        className,
      )}
      {...props}
    >
      {label}
    </span>
  ),
)
PolymorphicBadgePill.displayName = 'PolymorphicBadgePill'

export const PolymorphicBadge = React.forwardRef<HTMLSpanElement, PolymorphicBadgeProps>(
  ({ label = 'Poly', tooltip, className, ...props }, ref) => {
    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PolymorphicBadgePill ref={ref} label={label} className={className} {...props} />
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
    return <PolymorphicBadgePill ref={ref} label={label} className={className} {...props} />
  },
)
PolymorphicBadge.displayName = 'PolymorphicBadge'
